// 🔒🔒🔒 永久锁死代码 - Sidebar 组件 🔒🔒🔒
// ⚠️⚠️⚠️ 警告：此文件包含"永久锁死"的代码，绝对禁止随意修改?// 功能：侧边栏组件，包含项目存档按钮（任何人都可以在不载入图片的情况下查看历史存档?// 锁死日期?026-02-11
// 🔒🔒🔒 未经用户明确要求，绝对禁止修改锁死代?🔒🔒🔒

import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Fixture, type FixtureSelectOptions } from '../types';
import type { AuthLang } from './AuthModal';
import { t as tI18n, tReplace, tLegacy } from '../utils/i18n';
import { STRIP_CONTEXT_GROUPS, STRIP_CONTEXT_LABEL_ZH, type StripContext } from '../utils/stripLightContext';
import { DOWNLIGHT_CONTEXT_GROUPS, DOWNLIGHT_CONTEXT_LABEL_ZH, type DownlightContext } from '../utils/downlightContext';
interface SidebarProps {
  selectedFixtureIds: string[];
  fixtures: Fixture[];
  onSelectFixture: (id: string, opts?: FixtureSelectOptions) => void;
  updateSelectedFixtures: (updates: Partial<Fixture>) => void;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEnhance: () => void;
  /** 识别光源（与主区原「识别」按钮同源逻辑） */
  onRecognize?: () => void | Promise<void>;
  /** 当前任务 2 次识别已用完：禁用按钮，引导用户生成渲染或重新上传 */
  recognitionIdentifyExhausted?: boolean;
  /** 当前原图是否已跑过一次识别（为真时再次点击先出侧栏旁内联确认，不再用浏览器弹窗） */
  recognitionAlreadyCompleted?: boolean;
  /** 深度图 + 法线图已写入工程（侧栏呼吸绿线） */
  triPipelineSpatialReady?: boolean;
  /** 切换光源锁定（与画布光点双击联动，同源状态） */
  onToggleLock?: (id: string) => void;
  onReset: () => void;
  onDownload: () => void;
  onOpenHistory: () => void;
  onUpscale?: () => void;
  /** 色调映射（占位：可接面板或处理流程） */
  onToneMapping?: () => void;
  /** 变换角度（与色调映射同源全屏 UI，独立流程） */
  onAngleTransform?: () => void;
  /** 文本重构（占位：后续可接独立流程） */
  onTextReconstruct?: () => void;
  /** 物理重构（占位：后续可接独立流程） */
  onPhysicalReconstruct?: () => void;
  /** 深度（占位：可接面板或处理流程） */
  onDepth?: () => void;
  /** 在 Photopea 中打开当前渲染图（COS 公网 URL 或先上传再打开） */
  onOpenPhotopea?: () => void | Promise<void>;
  historyCount: number;
  isProcessing: boolean;
  renderingProgress?: number;
  renderingProgressSource?: 'generate' | 'command' | null;
  isScanning: boolean; // 图片分析状态
  hasImage: boolean;
  hasRendered: boolean;
  fixturesFound?: number;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  language: AuthLang;
  onLogout?: () => void;
  onOpenPaymentCenter?: () => void;
  onOpenUserAccount?: () => void;
  // 语种/语言切换（用于顶部红框位置）
  onSetLanguage: (lang: AuthLang) => void;
  onAddLightSource?: (lightType: string, label: string) => void; // 手动添加光源
  onConfirmLightSources?: () => void; // 🔥 新增：确认添加的光源
  isEditMode?: boolean; // 编辑模式
  selectedLightSourceType?: string | null; // 选中的光源类型
  manualStripContext?: string;
  onManualStripContextChange?: (ctx: StripContext) => void;
  manualDownlightContext?: string;
  onManualDownlightContextChange?: (ctx: DownlightContext) => void;
  onBackToSite?: () => void; // 返回官网（网关）
  /** 当前登录邮箱（以 App/Supabase 会话为准）；未传时回退 localStorage.userEmail（旧逻辑） */
  authenticatedUserEmail?: string | null;
  isAuthenticated?: boolean;
  lightingPresets?: { id: string; name: string }[];
  useLightingPresetForRender?: boolean;
  onUseLightingPresetForRenderChange?: (v: boolean) => void;
  selectedLightingPresetId?: string | null;
  onSelectedLightingPresetIdChange?: (id: string | null) => void;
}

/** 是否展示「光带/筒灯场景」下拉与两项 A/B/C 调试开关（产品要求默认隐藏） */
const SHOW_MANUAL_LIGHT_SCENE_AND_DEBUG_CONTROLS = false;

const FEEDBACK_EMAIL_ADDRESS = 'NemoAILab@163.com';

const Sidebar: React.FC<SidebarProps> = ({ 
  selectedFixtureIds,
  fixtures,
  onSelectFixture,
  updateSelectedFixtures, 
  onUpload, 
  onEnhance, 
  onRecognize,
  recognitionIdentifyExhausted = false,
  recognitionAlreadyCompleted = false,
  triPipelineSpatialReady = false,
  onToggleLock,
  onReset,
  onDownload,
  onOpenHistory,
  onUpscale,
  onToneMapping,
  onAngleTransform,
  onTextReconstruct,
  onPhysicalReconstruct,
  onDepth,
  onOpenPhotopea,
  historyCount,
  isProcessing,
  renderingProgress = 0,
  renderingProgressSource = null,
  isScanning,
  hasImage,
  hasRendered,
  fixturesFound = 0,
  isDarkMode,
  onToggleTheme,
  language,
  onLogout,
  onOpenPaymentCenter,
  onOpenUserAccount,
  onSetLanguage,
  onAddLightSource,
  onConfirmLightSources,
  isEditMode = false,
  selectedLightSourceType = null,
  manualStripContext = 'generic',
  onManualStripContextChange,
  manualDownlightContext = 'generic',
  onManualDownlightContextChange,
  onBackToSite,
  authenticatedUserEmail,
  isAuthenticated = false,
  lightingPresets = [],
  useLightingPresetForRender = false,
  onUseLightingPresetForRenderChange,
  selectedLightingPresetId = null,
  onSelectedLightingPresetIdChange,
}) => {
  // 🔥 使用 useRef 保存按钮引用，用于直接绑定点击事件
  const archiveButtonRef = useRef<HTMLButtonElement>(null);
  const generateButtonRef = useRef<HTMLButtonElement>(null);

  // 头像：右上角圆形按钮 + 12 图选择面板（按邮箱持久化）
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const avatarPanelRef = useRef<HTMLDivElement>(null);
  const [isAvatarOpen, setIsAvatarOpen] = useState(false);
  const [avatarId, setAvatarId] = useState<string>('01');
  const [pendingAvatarId, setPendingAvatarId] = useState<string>('01');
  const [username, setUsername] = useState<string>('');
  const [pendingUsername, setPendingUsername] = useState<string>('');
  const [avatarPanelPos, setAvatarPanelPos] = useState<{ top: number; right: number } | null>(null);

  type AvatarTransform = { scale: number; x: number; y: number };
  const [avatarTransforms, setAvatarTransforms] = useState<Record<string, AvatarTransform>>({});
  const [avatarMissing, setAvatarMissing] = useState<Record<string, boolean>>({});
  const [draggingAvatarId, setDraggingAvatarId] = useState<string | null>(null);
  const dragStartRef = useRef<{ id: string; x: number; y: number; startX: number; startY: number } | null>(null);

  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [feedbackEmailDialogOpen, setFeedbackEmailDialogOpen] = useState(false);

  const LANGS: { id: AuthLang; abbr: string; full: string }[] = [
    { id: 'en', abbr: 'EN', full: 'English' },
    { id: 'zh', abbr: '中', full: '中文' },
  ];

  const currentLangMeta = LANGS.find((l) => l.id === language) ?? LANGS[0];

  useEffect(() => {
    if (!isLangMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      const root = document.getElementById('nemo-lang-menu-root');
      if (root && root.contains(t)) return;
      setIsLangMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [isLangMenuOpen]);

  useEffect(() => {
    if (!feedbackEmailDialogOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFeedbackEmailDialogOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feedbackEmailDialogOpen]);

  const fromSession =
    typeof authenticatedUserEmail === 'string' && authenticatedUserEmail.includes('@')
      ? authenticatedUserEmail.trim()
      : '';
  const fromStorage =
    (typeof window !== 'undefined' ? localStorage.getItem('userEmail') : null)?.trim() || '';
  const userEmail = fromSession || fromStorage;
  const avatarStorageKey = `nemo_avatar_${userEmail || 'guest'}`;
  const usernameStorageKey = `nemo_username_${userEmail || 'guest'}`;
  const avatarTransformsKey = `nemo_avatar_transforms_${userEmail || 'guest'}`;
  const AVATAR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const id = String(i + 1).padStart(2, '0');
    return { id, src: `/avatars/${id}.png` };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem(avatarStorageKey);
    if (saved) {
      setAvatarId(saved);
      setPendingAvatarId(saved);
    }
    const savedName = localStorage.getItem(usernameStorageKey) || '';
    setUsername(savedName);
    setPendingUsername(savedName);

    // 头像裁切变换（缩放/平移）
    try {
      const savedTransforms = localStorage.getItem(avatarTransformsKey);
      if (savedTransforms) {
        const parsed = JSON.parse(savedTransforms);
        if (parsed && typeof parsed === 'object') setAvatarTransforms(parsed);
      }
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarStorageKey, usernameStorageKey, avatarTransformsKey]);

  useEffect(() => {
    if (!isAvatarOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (avatarPanelRef.current && avatarPanelRef.current.contains(t)) return;
      if (avatarBtnRef.current && avatarBtnRef.current.contains(t)) return;
      setIsAvatarOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [isAvatarOpen]);

  // 拖动平移头像（在圆形裁切中调整构图）
  useEffect(() => {
    if (!draggingAvatarId) return;
    const onMove = (e: MouseEvent) => {
      const st = dragStartRef.current;
      if (!st) return;
      const dx = e.clientX - st.startX;
      const dy = e.clientY - st.startY;
      const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
      const nextX = clamp(st.x + dx, -60, 60);
      const nextY = clamp(st.y + dy, -60, 60);
      setAvatarTransforms((prev) => ({
        ...prev,
        [st.id]: { ...(prev[st.id] || { scale: 1, x: 0, y: 0 }), x: nextX, y: nextY },
      }));
    };
    const onUp = () => {
      setDraggingAvatarId(null);
      dragStartRef.current = null;
    };
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
    return () => {
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
    };
  }, [draggingAvatarId]);

  // 打开时计算弹窗位置（挂到 body 顶层，避免被下面 UI 截断/盖住）
  useEffect(() => {
    if (!isAvatarOpen) return;
    const calc = () => {
      const el = avatarBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // 让弹窗右对齐头像按钮右边缘
      setAvatarPanelPos({
        top: Math.round(r.bottom + 10),
        right: Math.round(window.innerWidth - r.right),
      });
    };
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('scroll', calc, true);
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('scroll', calc, true);
    };
  }, [isAvatarOpen]);
  
  // 🔥 使用 useEffect 绑定点击事件，确保按钮可以点击
  useEffect(() => {
    const button = archiveButtonRef.current;
    if (button && onOpenHistory) {
      const handleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('[项目存档按钮] 🔥🔥🔥 ref 直接绑定点击事件触发 🔥🔥🔥');
        console.log('[项目存档按钮] 当前历史记录数量:', historyCount);
        console.log('[项目存档按钮] onOpenHistory 函数存在:', !!onOpenHistory);
        if (onOpenHistory) {
          console.log('[项目存档按钮] 通过 ref 调用 onOpenHistory...');
          try {
            onOpenHistory();
            console.log('[项目存档按钮] 通过 ref 调用成功');
          } catch (error) {
            console.error('[项目存档按钮] 通过 ref 调用失败:', error);
          }
        }
      };
      
      button.addEventListener('click', handleClick, { capture: true });
      
      return () => {
        button.removeEventListener('click', handleClick, { capture: true });
      };
    }
  }, [onOpenHistory, historyCount]);
  
  // 🔥 强制设置按钮样式，确保按钮正常显示（不受任何CSS影响）
  // 浅色模式：按需求与「生成」按钮互换样式 → 强制样式应用在生成按钮上
  // 深色模式：保持原逻辑（项目存档按钮保持黑底）
  useEffect(() => {
    const target = isDarkMode ? archiveButtonRef.current : generateButtonRef.current;
    if (target) {
      // 强制设置所有样式，确保按钮正常显示
      const setStyle = (prop: string, value: string) => {
        target.style.setProperty(prop, value, 'important');
      };
      
      setStyle('opacity', '1');
      setStyle('filter', 'none');
      setStyle('-webkit-filter', 'none');
      setStyle('color', 'rgb(255, 255, 255)');
      setStyle('background', '#0A0A0A');
      setStyle('border', '1px solid #262626');
      setStyle('border-radius', '0.375rem'); // 倒圆角缩减 50%：原 rounded-xl(12px) -> 6px
      setStyle('cursor', 'pointer');
      setStyle('pointer-events', 'auto');
      setStyle('z-index', '1001');
      setStyle('position', 'relative');
      setStyle('isolation', 'isolate');
      setStyle('mix-blend-mode', 'normal');
      
      // 与 GENERATE/高清放大 一致：保留 rounded-xl、p-4、布局与圆角
      target.className = 'w-full p-4 border border-[#262626] rounded-md bg-[#0A0A0A] flex items-center justify-between group hover:bg-neutral-900 hover:border-neutral-700 transition-all cursor-pointer';
      target.removeAttribute('disabled'); // 确保没有 disabled 属性
      // 检查父容器是否有 grayscale 样式
      let parent = target.parentElement;
      while (parent) {
        const computedStyle = window.getComputedStyle(parent);
        const filter = computedStyle.filter;
        const opacity = computedStyle.opacity;
        if (filter.includes('grayscale') || parseFloat(opacity) < 1) {
          console.warn('[项目存档按钮] ⚠️ 发现父容器有 grayscale 或 opacity 样式:', {
            filter,
            opacity,
            className: parent.className,
            element: parent
          });
        }
        parent = parent.parentElement;
      }
      
      console.log('[按钮样式] 🔥 强制设置按钮样式完成');
    }
  }, [isDarkMode]);
  
  // 🔥 持续监控按钮样式，确保不被覆盖
  useEffect(() => {
    const target = isDarkMode ? archiveButtonRef.current : generateButtonRef.current;
    if (!target) return;
    
    const observer = new MutationObserver(() => {
      // 如果样式被改变，立即恢复
      const computedStyle = window.getComputedStyle(target);
      if (computedStyle.filter.includes('grayscale') || parseFloat(computedStyle.opacity) < 1) {
        target.style.setProperty('opacity', '1', 'important');
        target.style.setProperty('filter', 'none', 'important');
        target.style.setProperty('-webkit-filter', 'none', 'important');
        target.style.setProperty('color', 'rgb(255, 255, 255)', 'important');
        target.style.setProperty('background', 'rgba(255, 255, 255, 0.10)', 'important');
        // 确保 className 不被移除
        if (!target.className.includes('text-white')) {
          target.className = 'w-full py-3 sm:py-4 font-black text-xs sm:text-sm tracking-widest uppercase transition-all relative overflow-hidden text-white min-h-[44px] sm:min-h-0';
        }
        console.log('[按钮样式] style override detected, restored.');
      }
    });
    
    observer.observe(target, {
      attributes: true,
      attributeFilter: ['style', 'class', 'disabled'],
      childList: false,
      subtree: false
    });
    
    return () => observer.disconnect();
  }, [isDarkMode]);
  
  // 🔥 本地状态管理输入框的值（用于手动输入）
  const [intensityInput, setIntensityInput] = useState<string>('');
  const [colorTempInput, setColorTempInput] = useState<string>('');
  const [hueInput, setHueInput] = useState<string>('');
  const [saturationInput, setSaturationInput] = useState<string>('100');
  const [lightnessInput, setLightnessInput] = useState<string>('50');
  /** 避免父级重渲染 / primaryFixture 引用变化时把正在键入的内容覆盖掉 */
  const intensityFieldFocusedRef = useRef(false);
  const colorTempFieldFocusedRef = useRef(false);
  const syncedPrimaryFixtureIdRef = useRef<string | null>(null);
  // 🔥 控制光源选项面板的显?隐藏
  const [showLightSourceOptions, setShowLightSourceOptions] = useState<boolean>(false);
  // 🔥 光源按钮严格单选：只记录当前激活的按钮索引
  const [activeLightSourceIndex, setActiveLightSourceIndex] = useState<number | null>(null);
  // A/B/C 调试开关（仅增加光源8种灯）
  const [manualDebug8Enabled, setManualDebug8Enabled] = useState<boolean>(false);
  // A/B/C 调试开关（仅识别出的槽灯/暗光带）
  const [autoCoveDebugEnabled, setAutoCoveDebugEnabled] = useState<boolean>(false);
  /** HSL ?RGB（H 0-360, S/L 0-100），用于显示?CRI 全光?*/
  const hslToRgb = (h: number, s: number, l: number) => {
    const H = ((h % 360) + 360) % 360 / 60;
    const S = Math.max(0, Math.min(100, s)) / 100;
    const L = Math.max(0, Math.min(100, l)) / 100;
    const C = (1 - Math.abs(2 * L - 1)) * S;
    const X = C * (1 - Math.abs((H % 2) - 1));
    const m = L - C / 2;
    let r = 0, g = 0, b = 0;
    if (H < 1) { r = C; g = X; b = 0; } else if (H < 2) { r = X; g = C; b = 0; } else if (H < 3) { r = 0; g = C; b = X; } else if (H < 4) { r = 0; g = X; b = C; } else if (H < 5) { r = X; g = 0; b = C; } else { r = C; g = 0; b = X; }
    return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
  };
  /** 色相 0?60 ?RGB，与少量白色混合?R、G、B 均有数?*/
  const hueToRgb = (h: number) => {
    const hue = ((h % 360) + 360) % 360;
    const c = 1, x = c * (1 - Math.abs((hue / 60) % 2 - 1)), m = 0;
    let r = 0, g = 0, b = 0;
    if (hue < 60) { r = c; g = x; b = 0; }
    else if (hue < 120) { r = x; g = c; b = 0; }
    else if (hue < 180) { r = 0; g = c; b = x; }
    else if (hue < 240) { r = 0; g = x; b = c; }
    else if (hue < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    const R = Math.round((r + m) * 255);
    const G = Math.round((g + m) * 255);
    const B = Math.round((b + m) * 255);
    const mix = 18;
    return {
      r: Math.max(0, Math.min(255, Math.round(R * (1 - mix / 255) + mix))),
      g: Math.max(0, Math.min(255, Math.round(G * (1 - mix / 255) + mix))),
      b: Math.max(0, Math.min(255, Math.round(B * (1 - mix / 255) + mix)))
    };
  };

  const getKelvinString = (temp: number) => {
    const neutral = language === 'en' ? '6500K NEUTRAL' : '6500K 中性';
    const warm = language === 'en' ? 'K WARM' : 'K 暖色';
    const cool = language === 'en' ? 'K COOL' : 'K 冷色';
    
    if (temp === 0) return neutral;
    if (temp < 0) return `${Math.round(6500 + (temp * 90))}${warm}`;
    return `${Math.round(6500 + (temp * 170))}${cool}`;
  };

  /** 与侧栏滑条 / geminiService getColorTempInfo 一致：冷端最高约 15000K */
  const getCurrentKelvinFromColorTemp = (temp: number) => {
    if (temp === 0) return 6500;
    if (temp < 0) return Math.max(2000, Math.min(6500, Math.round(6500 + temp * 90)));
    return Math.max(6500, Math.min(15000, Math.round(6500 + temp * 170)));
  };

  /** 列表摘要 K：已调整过的灯必须与侧栏一致，不能仍显示识别时的 detectedKelvin */
  const getListKelvinSummary = (fixture: Fixture) => {
    if (fixture.isModified) {
      return getCurrentKelvinFromColorTemp(Number(fixture.colorTemp) || 0);
    }
    const k = Number((fixture as any).detectedKelvin);
    if (Number.isFinite(k) && k >= 1500 && k <= 15000) return Math.round(k);
    return getCurrentKelvinFromColorTemp(Number(fixture.colorTemp) || 0);
  };

  const t = (en: string, zh: string) => tLegacy(en, zh, language);
  const tK = (key: Parameters<typeof tI18n>[0]) => tI18n(key, language);

  const primaryFixture = fixtures.find((f) => f.id === selectedFixtureIds[0]) || null;
  const isMultipleSelected = selectedFixtureIds.length > 1;

  // 同步 primaryFixture 到本地输入框状态（聚焦强度/色温输入时不覆盖当前键入）
  useEffect(() => {
    const id = primaryFixture?.id ?? null;

    if (!primaryFixture) {
      syncedPrimaryFixtureIdRef.current = null;
      setIntensityInput('0');
      setColorTempInput('6500');
      setHueInput('0');
      setSaturationInput('100');
      setLightnessInput('50');
      return;
    }

    const fixtureBecamePrimary = id !== syncedPrimaryFixtureIdRef.current;
    if (fixtureBecamePrimary) {
      syncedPrimaryFixtureIdRef.current = id;
    }

    if (fixtureBecamePrimary || !intensityFieldFocusedRef.current) {
      setIntensityInput(Math.round(primaryFixture.intensity * 100).toString());
    }

    if (fixtureBecamePrimary || !colorTempFieldFocusedRef.current) {
      const temp = primaryFixture.colorTemp;
      if (temp === 0) {
        setColorTempInput('6500');
      } else if (temp < 0) {
        setColorTempInput(Math.round(6500 + temp * 90).toString());
      } else {
        setColorTempInput(Math.round(6500 + temp * 170).toString());
      }
    }

    if (fixtureBecamePrimary) {
      setHueInput(String(primaryFixture.hue ?? 0));
      setSaturationInput(String(primaryFixture.saturation ?? 100));
      setLightnessInput(String(primaryFixture.lightness ?? 50));
    }
  }, [primaryFixture]);

  // 灯具类型 key 与 i18n 中的 fixture_* 一致，随当前语种显示
  const getTypeName = (type: string) => {
    const keyMap: Record<string, string> = {
      'sunlight': 'fixture_sunlight',
      'natural_light': 'fixture_natural_light',
      'ambient_light': 'fixture_ambient_light',
      'wall_lamp': 'fixture_wall_lamp',
      'pendant': 'fixture_pendant',
      'table_lamp': 'fixture_table_lamp',
      'floor_lamp': 'fixture_floor_lamp',
      'spotlight': 'fixture_spotlight',
      'downlight': 'fixture_downlight',
      'window': 'fixture_window',
      'window_light': 'fixture_window',
      'candle': 'fixture_candle',
      'cove_light': 'fixture_cove_light',
      'cove_lighting': 'fixture_cove_light',
      'other': 'fixture_other',
    };
    const key = keyMap[type];
    if (key) return tK(key as Parameters<typeof tI18n>[0]);
    return type.replace(/_/g, ' ').toUpperCase();
  };

  // 获取显示名称：始终按当前语种显示，与语种切换一致
  const getDisplayName = (fixture: { type: string; label?: string }): string => {
    return getTypeName(fixture.type);
  };

  const hexToRgba = (hex: string, alpha: number) => {
    const safe = hex.replace('#', '');
    const normalized = safe.length === 3 ? safe.split('').map((c) => c + c).join('') : safe;
    const r = parseInt(normalized.slice(0, 2), 16) || 0;
    const g = parseInt(normalized.slice(2, 4), 16) || 0;
    const b = parseInt(normalized.slice(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // 图层与“已调整”标签统一使用该颜色映射（亮度/饱和度与原黄色同级）
  const getLightTypeAccent = (type: string, label?: string) => {
    const t = (type || '').toLowerCase();
    const l = (label || '').toLowerCase();
    if (t === 'natural_light' || t === 'window' || t === 'window_light' || t === 'sunlight' || l.includes('window') || l.includes('natural')) return '#79AFFF';
    if (t === 'ambient_light') return '#69D1C4';
    if (t === 'cove_light' || t === 'cove_lighting') return '#80C7FF';
    if (t === 'spotlight') return '#E2BF78';
    if (t === 'wall_lamp') return '#E7A98A';
    if (t === 'table_lamp') return '#D9B1EE';
    if (t === 'floor_lamp') return '#D7CDA1';
    if (t === 'downlight' || t === 'pendant') return '#D7CDA1';
    return '#D7CDA1';
  };

  // 手动光源图标：光带=横线；筒灯=嵌入式筒灯（圆环+灯腔）
  const LightSourceIcon: React.FC<{ type: string }> = ({ type }) => {
    const t = (type || '').toLowerCase();
    const svgProps = {
      xmlns: 'http://www.w3.org/2000/svg' as const,
      width: 20,
      height: 20,
      viewBox: '0 0 24 24',
      fill: 'none' as const,
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round' as const,
      strokeLinejoin: 'round' as const,
    };
    if (t === 'cove_light' || t === 'cove_lighting') {
      return (
        <svg {...svgProps}>
          <line x1="3" y1="12" x2="21" y2="12" strokeWidth="2.5" />
        </svg>
      );
    }
    if (t === 'downlight') {
      return (
        <svg {...svgProps}>
          <ellipse cx="12" cy="7" rx="7.5" ry="2.2" />
          <path d="M7.2 9.2c.8 3.2 3.4 5.3 4.8 5.3s4-2.1 4.8-5.3" />
          <circle cx="12" cy="10.5" r="2.8" />
          <line x1="12" y1="13.3" x2="12" y2="16.5" opacity={0.85} />
          <path d="M9.5 17h5l-1.2 3.5h-2.6L9.5 17z" opacity={0.75} />
        </svg>
      );
    }
    const fp = { ...svgProps, width: 18, height: 18 };
    return (
      <svg {...fp}>
        <circle cx="12" cy="12" r="3.5" />
      </svg>
    );
  };

  // 手动加光源：上下排列（上=光带、下=筒灯）；每行内图标+文字横向；宽度与「光带场景」select 一致（mx-5）
  const lightSourceTypes: Array<{ type: string; labelKey: string; bgColor?: string }> = [
    { type: 'cove_light', labelKey: 'add_light_backlit', bgColor: 'light-blue' },
    { type: 'downlight', labelKey: 'add_light_downlight', bgColor: 'light-yellow' },
  ];

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const fromWindow = !!(window as any).__NEMO_MANUAL_LIGHT_DEBUG_8;
    const fromStorage = localStorage.getItem('nemo_manual_light_debug_8') === '1';
    const initial = fromWindow || fromStorage;
    (window as any).__NEMO_MANUAL_LIGHT_DEBUG_8 = initial;
    setManualDebug8Enabled(initial);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__NEMO_MANUAL_LIGHT_DEBUG_8 = manualDebug8Enabled;
    localStorage.setItem('nemo_manual_light_debug_8', manualDebug8Enabled ? '1' : '0');
  }, [manualDebug8Enabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const fromWindow = !!(window as any).__NEMO_AUTO_COVE_DEBUG;
    const fromStorage = localStorage.getItem('nemo_auto_cove_debug') === '1';
    const initial = fromWindow || fromStorage;
    (window as any).__NEMO_AUTO_COVE_DEBUG = initial;
    setAutoCoveDebugEnabled(initial);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__NEMO_AUTO_COVE_DEBUG = autoCoveDebugEnabled;
    localStorage.setItem('nemo_auto_cove_debug', autoCoveDebugEnabled ? '1' : '0');
  }, [autoCoveDebugEnabled]);
  const canRecognizeNow =
    !!hasImage && !isScanning && !isProcessing && !recognitionIdentifyExhausted;
  /** 额度用尽且当前不在识别/渲染中：显示「识别已关闭」强提示（分析中仍显示普通文案，避免误导） */
  const showRecognizeLockedUi =
    recognitionIdentifyExhausted && !isScanning && !isProcessing;
  // 仅在“已载图但尚未识别出光点”时呼吸；额度用尽或识别中不呼吸，避免误导
  const shouldRecognizeBreathe =
    !!hasImage &&
    !isScanning &&
    !isProcessing &&
    !recognitionIdentifyExhausted &&
    (fixturesFound ?? 0) === 0;
  const actionDotClass = (color: string, muted: boolean) =>
    `w-2 h-2 rounded-full ${muted ? 'opacity-35' : 'opacity-100'} ${color}`;

  const recognizeRowRef = useRef<HTMLDivElement>(null);
  const [recognizeAgainOpen, setRecognizeAgainOpen] = useState(false);
  const [recognizePopStyle, setRecognizePopStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (!recognizeAgainOpen) return;
    const el = recognizeRowRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      const panelW = 216;
      let left = r.left - panelW - 10;
      if (left < 8) left = 8;
      if (left + panelW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - panelW - 8);
      const top = Math.max(16, Math.min(window.innerHeight - 16, r.top + r.height / 2));
      setRecognizePopStyle({
        position: 'fixed',
        left,
        top,
        transform: 'translateY(-50%)',
        width: panelW,
        zIndex: 200000,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [recognizeAgainOpen]);

  useEffect(() => {
    if (isScanning) setRecognizeAgainOpen(false);
  }, [isScanning]);

  useEffect(() => {
    if (recognitionIdentifyExhausted) setRecognizeAgainOpen(false);
  }, [recognitionIdentifyExhausted]);

  useEffect(() => {
    if (!recognizeAgainOpen) return;
    const onDown = (e: MouseEvent) => {
      const n = e.target as Node;
      if (recognizeRowRef.current?.contains(n)) return;
      const p = document.getElementById('nemo-recognize-again-popover');
      if (p?.contains(n)) return;
      setRecognizeAgainOpen(false);
    };
    const t = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [recognizeAgainOpen]);

  // 退出编辑模式时，清空按钮高亮
  useEffect(() => {
    if (!isEditMode) {
      setActiveLightSourceIndex(null);
    }
  }, [isEditMode]);

  return (
    <>
    <div
      className={`
        w-full sm:w-[339px] lg:w-[389px]
        h-full min-h-0
        overflow-hidden
        transition-colors duration-300
        ${isDarkMode ? 'bg-black border-[#262626]' : 'bg-zinc-50 border-zinc-200'}
        border-l
        pl-[8px] pr-[14px]
        flex flex-col shrink-0
        z-20
      `}
    >
      {/* 单一大滚动区：禁止横向滚动避免底部黑条；滚动条样式随主题 */}
      <div
        className={`sidebar-main-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`}
        style={{
          scrollbarColor: isDarkMode ? 'rgba(0,0,0,0.7) rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.5) rgba(0,0,0,0.06)',
          scrollbarWidth: 'thin',
        }}
      >
      <style>{`
        /* 与官网一致：Inter 16px 字重400 */
        .sidebar-btn-official-font { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'San Francisco', 'Helvetica Neue', Helvetica, Arial, sans-serif !important; font-size: 16px !important; font-weight: 400 !important; }
        .sidebar-btn-official-font.sidebar-btn-label { font-size: 16px !important; line-height: 1.25 !important; }
        .sidebar-btn-official-font.sidebar-btn-label.sidebar-btn-label-95 { font-size: 15.2px !important; }
        /* 蓝框内字体：?8px 基础上再放大 30% = 10.4px */
        .sidebar-blue-box { font-size: 10.4px !important; line-height: 1.4 !important; }
        /* 蓝框内文字：先缩?30% 再在现有基础上增?10%?1.2px?2.32px?*/
        .sidebar-blue-box-smaller { font-size: 12.32px !important; }
        /* 底部蓝框（NEMOLIGHTLAB + 反馈）：在现有基础上整体缩?20% */
        .sidebar-footer-blue-box { font-size: 9.86px !important; line-height: 1.4 !important; }
        /* 色相/饱和?亮度标签：在 9px 基础上增?15% = 10.35px */
        .sidebar-hsl-label { font-size: 10.35px !important; }
        /* 饱和?亮度/色相 三行滑杆纵向间距：强制用 gap 生效，约 12px（原?6px ?2 倍） */
        .sidebar-hsl-rows { display: flex !important; flex-direction: column !important; gap: 20px !important; }
        /* 指定滑杆圆形按钮：最外圈亮黄色（用于强度/色温，不影响颜色模块） */
        .sidebar-warm-thumb {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
        }
        .sidebar-warm-thumb::-webkit-slider-runnable-track {
          height: 0;
          background: transparent;
        }
        .sidebar-warm-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #a3a3a3;
          cursor: pointer;
          border: 5px solid #D7CDA1 !important;
          box-shadow: none !important;
        }
        .sidebar-warm-thumb::-moz-range-track {
          height: 0;
          background: transparent;
        }
        .sidebar-warm-thumb::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #a3a3a3;
          cursor: pointer;
          border: 5px solid #D7CDA1 !important;
          box-shadow: none !important;
        }
        .sidebar-color-hue-slider { -webkit-appearance: none; appearance: none; background: transparent; }
        .sidebar-color-hue-slider::-webkit-slider-runnable-track { height: 0; background: transparent; }
        .sidebar-color-hue-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%; border: 2px solid #fff; background: rgba(0,0,0,0.3); box-shadow: none; cursor: pointer; }
        .sidebar-color-hue-slider::-moz-range-track { height: 0; background: transparent; }
        .sidebar-color-hue-slider::-moz-range-thumb { width: 14px; height: 14px; border-radius: 50%; border: 2px solid #fff; background: rgba(0,0,0,0.3); box-shadow: none; cursor: pointer; }
        /* 侧边栏主滚动条：随主题，浅色模式不用黑轨避免黑条 */
        .sidebar-main-scroll::-webkit-scrollbar { width: 8px; }
        .sidebar-main-scroll::-webkit-scrollbar-track { background: ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}; border-radius: 4px; }
        .sidebar-main-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.55); border-radius: 4px; }
        .sidebar-main-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.75); }
        .sidebar-main-scroll::-webkit-scrollbar-corner { background: ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}; }
        @keyframes recognize-btn-breathe {
          0%, 100% {
            transform: scale(1);
            box-shadow: none;
            border-color: ${isDarkMode ? '#2f2f2f' : '#d4d4d8'};
          }
          50% {
            transform: scale(1.012);
            box-shadow: none;
            border-color: #D7CDA1;
          }
        }
        .recognize-btn-breathe {
          animation: recognize-btn-breathe 2.25s ease-in-out infinite;
          will-change: transform, border-color;
        }
        .recognize-icon-breathe {
          animation: recognize-btn-breathe 2.25s ease-in-out infinite;
          will-change: transform, border-color;
        }
      `}</style>
      {/* 右侧边栏所有内容放在一个容器内；isolation 避免拖动滑杆时按钮与 header 层叠重叠；背景与侧边栏一?*/}
      <div className={`flex flex-col flex-1 min-h-0 isolate ${isDarkMode ? 'bg-black' : 'bg-zinc-50'}`}>
      {/* Header：sticky 保持可见，滚动条从屏幕最上延伸到?*/}
      <header className={`shrink-0 pl-[20px] pr-[14px] pt-6 pb-2 border-b transition-colors ${isDarkMode ? 'bg-black border-[#262626]/50' : 'bg-zinc-50 border-zinc-200'}`}>
        <div className="grid grid-cols-[1fr_auto] items-start gap-x-3 gap-y-2">
          <div className="flex items-center gap-2 select-none shrink-0" style={{ fontFamily: 'Inter, sans-serif' }}>
            <svg className="shrink-0 text-[#D7CDA1] flex-shrink-0" width={32} height={16} viewBox="0 0 500 250" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden style={{ transform: 'rotate(5deg)' }}>
              <path d="M50,125 C100,50 350,50 400,125 C350,200 100,200 50,125 Z" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M85,100 C95,110 95,140 85,150" stroke="currentColor" strokeWidth="3" />
              <path d="M100,90 C115,110 115,140 100,160" stroke="currentColor" strokeWidth="3" />
              <circle cx="75" cy="115" r="5" stroke="currentColor" strokeWidth="2" />
              <g stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M130,160 L130,90 L170,160 L170,90" />
                <path d="M190,160 L190,90 L230,90 M190,125 L220,125 M190,160 L230,160" />
                <path d="M250,160 L250,90 L280,130 L310,90 L310,160" />
                <ellipse cx="350" cy="125" rx="25" ry="35" />
              </g>
              <path d="M400,125 L470,80 L470,170 Z" stroke="currentColor" strokeWidth="4" />
            </svg>
            <span className="text-xl font-bold uppercase inline-flex items-baseline" style={{ letterSpacing: '-0.072em' }}>
              <span className={isDarkMode ? 'text-white' : 'text-zinc-900'}>NEMO</span>
              <span className="text-[#D7CDA1]">LIGHT</span>
              <span className={isDarkMode ? 'text-white' : 'text-zinc-900'}>LAB</span>
            </span>
          </div>
          <div className="flex items-start gap-[10px] shrink-0 relative" style={{ transform: 'translateX(-40px)', transformOrigin: 'top right' }}>
            {/* 右上角头像按钮（截图红圈） */}
            <div className="flex flex-col items-center">
              <button
                ref={avatarBtnRef}
                type="button"
                onClick={() => {
                  const hasEmail = !!(userEmail && userEmail.includes('@'));
                  const loggedInCanLogout = !!(isAuthenticated && onLogout);
                  if (!hasEmail && !loggedInCanLogout) {
                    alert(tK('login_required_avatar'));
                    return;
                  }
                  setPendingAvatarId(avatarId);
                  setPendingUsername(username);
                  setIsAvatarOpen((v) => !v);
                }}
                className={`w-[42px] h-[42px] rounded-full border flex items-center justify-center overflow-hidden transition-colors ${
                  isDarkMode
                    ? 'border-[#262626] bg-neutral-900/60 hover:border-[#D7CDA1]/60'
                    : 'border-zinc-300 bg-white hover:border-[#D7CDA1]/60'
                }`}
                title={tK('avatar')}
                aria-label={tK('avatar')}
              >
                {/* 用可变换的 <img> 渲染头像（默认完整可见，滚轮缩放/拖动平移微调构图） */}
                <div className="w-full h-full rounded-full overflow-hidden">
                  <img
                    src={`/avatars/${avatarId}.png`}
                    alt={tK('avatar')}
                    draggable={false}
                    className="w-full h-full select-none pointer-events-none"
                    style={{
                      objectFit: 'contain',
                      transform: (() => {
                        const tr = avatarTransforms[avatarId] || { scale: 1, x: 0, y: 0 };
                        return `translate(${tr.x}px, ${tr.y}px) scale(${tr.scale})`;
                      })(),
                      transformOrigin: 'center',
                    }}
                    onError={() => setAvatarMissing((p) => ({ ...p, [avatarId]: true }))}
                    onLoad={() => setAvatarMissing((p) => ({ ...p, [avatarId]: false }))}
                  />
                </div>
                {!!avatarMissing[avatarId] && (
                  <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 8.25a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 20.25a8.25 8.25 0 0115 0" />
                  </svg>
                )}
              </button>

              {username && (
                <div
                  className={`mt-2 text-center sidebar-btn-official-font sidebar-blue-box ${
                    isDarkMode ? 'text-white' : 'text-zinc-900'
                  }`}
                  style={{ lineHeight: 1 }}
                >
                  {username}
                </div>
              )}
            </div>

            {isAvatarOpen && typeof document !== 'undefined' && avatarPanelPos
              ? createPortal(
              <div className="fixed inset-0 z-[99999] pointer-events-none">
              <div
                ref={avatarPanelRef}
                className={`pointer-events-auto fixed w-[320px] rounded-[12px] border shadow-2xl p-4 ${
                  isDarkMode ? 'bg-[#0A0A0A] border-[#262626]' : 'bg-white border-zinc-200'
                }`}
                style={{
                  top: avatarPanelPos.top,
                  right: avatarPanelPos.right,
                  backdropFilter: 'blur(14px)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex flex-col">
                    <div className={`sidebar-btn-official-font ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                      {tK('choose_avatar')}
                    </div>
                    <div className={`text-xs mt-1 ${isDarkMode ? 'text-neutral-500' : 'text-zinc-500'}`}>
                      {userEmail ||
                        (isAuthenticated ? (language === 'zh' ? '已登录' : 'Signed in') : '')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsAvatarOpen(false)}
                    className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors ${
                      isDarkMode ? 'border-[#262626] text-neutral-400 hover:text-white hover:bg-neutral-900' : 'border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50'
                    }`}
                    aria-label="Close"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-4">
                  {AVATAR_OPTIONS.map((opt) => {
                    const active = pendingAvatarId === opt.id;
                    const tr = avatarTransforms[opt.id] || { scale: 1, x: 0, y: 0 };
                    const missing = !!avatarMissing[opt.id];
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPendingAvatarId(opt.id)}
                        className={`relative w-full aspect-square rounded-[10px] overflow-hidden border transition-colors ${
                          active ? 'border-[#D7CDA1]' : isDarkMode ? 'border-[#262626] hover:border-[#D7CDA1]/60' : 'border-zinc-200 hover:border-[#D7CDA1]/60'
                        }`}
                        title={`${tK('avatar')} ${opt.id}`}
                      >
                        <div className={`absolute inset-0 ${isDarkMode ? 'bg-neutral-900' : 'bg-zinc-100'}`} />

                        {/* 圆形裁切 + 悬停拖动/滚轮缩放预览 */}
                        <div
                          className="absolute inset-0 flex items-center justify-center"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPendingAvatarId(opt.id);
                            setDraggingAvatarId(opt.id);
                            dragStartRef.current = { id: opt.id, x: tr.x, y: tr.y, startX: e.clientX, startY: e.clientY };
                          }}
                          onWheel={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPendingAvatarId(opt.id);
                            const delta = e.deltaY;
                            const step = delta > 0 ? -0.08 : 0.08;
                            const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
                            const nextScale = clamp(tr.scale + step, 1, 3);
                            setAvatarTransforms((prev) => ({
                              ...prev,
                              [opt.id]: { ...(prev[opt.id] || { scale: 1, x: 0, y: 0 }), scale: nextScale },
                            }));
                          }}
                          style={{ cursor: draggingAvatarId === opt.id ? 'grabbing' : 'grab' }}
                        >
                          <div className="w-[56px] h-[56px] rounded-full overflow-hidden border border-white/5">
                            <img
                              src={opt.src}
                              alt={`${tK('avatar')} ${opt.id}`}
                              draggable={false}
                              className="w-full h-full select-none pointer-events-none"
                              style={{
                                objectFit: 'contain',
                                transform: `translate(${tr.x}px, ${tr.y}px) scale(${tr.scale})`,
                                transformOrigin: 'center',
                              }}
                              onError={() => setAvatarMissing((p) => ({ ...p, [opt.id]: true }))}
                              onLoad={() => setAvatarMissing((p) => ({ ...p, [opt.id]: false }))}
                            />
                          </div>
                        </div>

                        {/* 图片未放入时才显示编号；且不拦截鼠标事件 */}
                        {missing && (
                          <div className={`pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] font-semibold ${isDarkMode ? 'text-neutral-500' : 'text-zinc-500'}`}>
                            {opt.id}
                          </div>
                        )}
                        {active && (
                          <div className="absolute right-1 top-1 w-5 h-5 rounded-full bg-[#D7CDA1] text-black flex items-center justify-center">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* 用户名输入（左对齐到 09 列）+ 确定按钮 */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 relative">
                    <input
                      value={pendingUsername}
                      onChange={(e) => {
                        const v = e.target.value;
                        // 最多 16 个字符（包含空格）；不做强制过滤，确认时校验
                        if (v.length <= 16) setPendingUsername(v);
                      }}
                      placeholder={tK('username_placeholder')}
                      className={`w-full h-[40px] rounded-md border px-3 pr-[58px] text-sm outline-none transition-colors ${
                        isDarkMode
                          ? 'bg-[#0A0A0A] border-[#262626] text-white placeholder:text-neutral-600 focus:border-[#D7CDA1]/60'
                          : 'bg-white border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:border-[#D7CDA1]/60'
                      }`}
                      aria-label="username"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const nextName = (pendingUsername || '').trim();
                      const isValid = /^[A-Za-z ]{1,16}$/.test(nextName);
                      if (!isValid) {
                        alert(tK('username_invalid'));
                        return;
                      }

                      // 年度修改次数限制：每年最多 3 次（仅当名字发生变化时计数）
                      const year = new Date().getFullYear();
                      const yearKey = `nemo_username_changes_${userEmail || 'guest'}_${year}`;
                      const used = typeof window !== 'undefined' ? (parseInt(localStorage.getItem(yearKey) || '0', 10) || 0) : 0;
                      const changed = nextName !== (username || '');
                      if (changed && used >= 3) {
                        alert(tK('username_limit_reached'));
                        return;
                      }

                      setAvatarId(pendingAvatarId);
                      setUsername(nextName);
                      if (typeof window !== 'undefined') {
                        localStorage.setItem(avatarStorageKey, pendingAvatarId);
                        localStorage.setItem(usernameStorageKey, nextName);
                        localStorage.setItem(avatarTransformsKey, JSON.stringify(avatarTransforms));
                        if (changed) localStorage.setItem(yearKey, String(used + 1));
                      }
                      setIsAvatarOpen(false);
                    }}
                    className="px-6 h-[40px] rounded-md text-sm font-bold bg-[#D7CDA1] text-black hover:opacity-90 transition-opacity shrink-0"
                  >
                    {tK('confirm')}
                  </button>
                </div>

                {/* 退出登录：移动到面板最下方横向最长条 */}
                {onLogout && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsAvatarOpen(false);
                      onLogout();
                    }}
                    className={`w-full h-[44px] rounded-md border-0 flex items-center justify-center gap-2 font-semibold transition-colors outline-none focus-visible:ring-0 ${
                      isDarkMode
                        ? 'text-red-400 bg-neutral-900/30 hover:bg-neutral-900/60'
                        : 'text-red-600 bg-zinc-50 hover:bg-red-50'
                    }`}
                    title={tK('logout')}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-sm">{tK('logout')}</span>
                  </button>
                )}
              </div>
              </div>,
              document.body
              )
              : null}
          </div>

          {/* 高保真说明 + 按钮：与右侧用户名同一行对齐 */}
          <div className="col-start-1 row-start-2 -mt-[14px]">
            <div className="space-y-1 sidebar-btn-official-font sidebar-blue-box text-neutral-500 leading-relaxed">
              <div className="flex items-center gap-[7px] -translate-y-[10px]">
                <span className="w-1.5 h-1.5 bg-[#D7CDA1] rounded-full animate-pulse shrink-0" />
                <span>{tK('high_fidelity_engine')}</span>
              </div>
              {/* 官网 / 深浅模式：此处仅保留这两个按钮 */}
              <div className="pt-2 flex items-center gap-[7px]">
                {onBackToSite && (
                  <button
                    type="button"
                    onClick={onBackToSite}
                    className={`h-8 min-w-[118px] px-2 rounded-md border-0 outline-none focus-visible:ring-0 flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? 'text-neutral-300 bg-neutral-900/50 hover:text-black hover:bg-[#D7CDA1]'
                        : 'text-zinc-700 bg-white hover:text-black hover:bg-[#F4E4C2]'
                    }`}
                    title={tK('back_to_site')}
                  >
                    <span className="text-[11px] font-semibold tracking-wide leading-none whitespace-nowrap">
                      {tK('back_to_site')}
                    </span>
                  </button>
                )}
                <button
                  onClick={onToggleTheme}
                  className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors ${isDarkMode ? 'border-[#262626] text-neutral-400 bg-neutral-900/50 hover:text-white hover:bg-neutral-800' : 'border-zinc-300 text-zinc-600 bg-white hover:bg-zinc-100 hover:text-zinc-900'}`}
                  title={tK('toggle_theme')}
                >
                  {isDarkMode ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                      />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <circle cx="12" cy="12" r="4" strokeWidth={2} />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 2v2m0 16v2M4.93 4.93l1.414 1.414M17.657 17.657l1.414 1.414M2 12h2m16 0h2M4.93 19.07l1.414-1.414M17.657 6.343l1.414-1.414"
                      />
                    </svg>
                  )}
                </button>

                {/* 语种/语言按钮：放在深浅模式按钮右侧 */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLangMenuOpen((v) => !v);
                      setIsAvatarOpen(false);
                    }}
                    className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? 'border-[#262626] text-neutral-300 bg-neutral-900/50 hover:text-white hover:bg-neutral-800'
                        : 'border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-100 hover:text-zinc-900'
                    }`}
                    title={`Language: ${currentLangMeta.full}`}
                    aria-label="Language menu"
                  >
                    <span className="text-[11px] font-bold tracking-wide">{currentLangMeta.abbr}</span>
                  </button>

                  {isLangMenuOpen && (
                    <div
                      id="nemo-lang-menu-root"
                      className={`absolute z-[100000] mt-[10px] right-0 w-[170px] rounded-[12px] border shadow-2xl overflow-hidden ${
                        isDarkMode ? 'bg-[#0A0A0A] border-[#262626]' : 'bg-white border-zinc-200'
                      }`}
                    >
                      <div className={`px-3 py-2 text-xs font-semibold tracking-wide ${isDarkMode ? 'text-neutral-500' : 'text-zinc-500'}`}>
                        Language / 语言
                      </div>
                      <div className="max-h-[260px] overflow-y-auto">
                        {LANGS.map((l) => (
                          <button
                            key={l.id}
                            type="button"
                            onClick={() => {
                              onSetLanguage(l.id);
                              setIsLangMenuOpen(false);
                            }}
                            className={`w-full px-3 py-2 flex items-center justify-between text-sm transition-colors ${
                              language === l.id
                                ? isDarkMode
                                  ? 'bg-white/10 text-white'
                                  : 'bg-zinc-100 text-zinc-900'
                                : isDarkMode
                                  ? 'text-neutral-300 hover:bg-white/5'
                                  : 'text-zinc-700 hover:bg-zinc-50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <span className="w-[28px] text-center text-xs font-bold" style={{ color: '#D7CDA1' }}>
                                {l.abbr}
                              </span>
                              <span>{l.full}</span>
                            </span>
                            <span className={language === l.id ? 'text-[#D7CDA1]' : isDarkMode ? 'text-neutral-500' : 'text-zinc-400'}>
                              {language === l.id ? '✓' : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
        {/* LOAD PROJECT：与蓝框同容器（header 内） */}
        <input
          type="file"
          id="img-upload"
          className="hidden"
          accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
          onChange={onUpload}
        />
        <label
          htmlFor="img-upload"
          className={`
            w-full min-h-[72px] py-[14.4px] pl-[9px] pr-[14px] rounded-md flex items-center justify-between relative overflow-hidden mt-[23px] select-none transition-all group
            ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A]' : 'border border-zinc-300 bg-white'}
            ${isProcessing || isScanning
              ? 'opacity-80 cursor-default pointer-events-none'
              : `cursor-pointer ${isDarkMode ? 'hover:border-[#D7CDA1]' : 'hover:bg-zinc-50 hover:border-[#D7CDA1]'}`}
          `}
        >
          <div className="flex items-center gap-[13px]">
            <div className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </div>
            <span className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-white group-hover:text-[#D7CDA1]' : 'text-zinc-900 group-hover:text-zinc-900'}`}>
              {language === 'zh' ? '加载图片' : tK('load_project_btn')}
            </span>
          </div>
          <div className={`flex items-center justify-center shrink-0 transition-colors ${isDarkMode ? 'text-neutral-700 group-hover:text-white' : 'text-zinc-500 group-hover:text-zinc-900'}`} style={{ transform: 'scaleX(0.9)' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </label>
      </header>

      {/* 灯光图层内容区（由上方单一大滚动区统一滚动?*/}
      <div className="px-[20px] pt-[12px] pb-6 pr-[14px] space-y-8 shrink-0">
      {hasImage && fixtures.length > 0 && (
        <div className="space-y-4 flex-shrink-0 flex flex-col" style={{ minHeight: 0 }}>
          <div className="flex items-center justify-between flex-shrink-0">
            <h3 className={`sidebar-btn-official-font sidebar-blue-box-smaller ${isDarkMode ? 'text-white/40' : 'text-zinc-600'}`} style={{ fontSize: '90%' }}>
              {tK('detected_fixtures')}
            </h3>
            <span className="sidebar-btn-official-font sidebar-blue-box-smaller text-[#D7CDA1]" style={{ fontSize: '90%' }}>
              {tK('hold_ctrl_multi')}
            </span>
          </div>
          <div 
            className="flex-1 overflow-y-auto pr-[7px] space-y-2"
            style={{
              maxHeight: '400px',
              minHeight: '120px',
              scrollbarWidth: 'thin',
              scrollbarColor: isDarkMode ? 'rgba(0, 0, 0, 0.6) rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.5) rgba(0, 0, 0, 0.1)',
            }}
          >
            <style>{`
              .fixture-list-container::-webkit-scrollbar {
                width: 8px;
              }
              .fixture-list-container::-webkit-scrollbar-track {
                background: ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
                border-radius: 4px;
              }
              .fixture-list-container::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.55);
                border-radius: 4px;
                border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
              }
              .fixture-list-container::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 0, 0, 0.75);
              }
              .fixture-list-container::-webkit-scrollbar-thumb:active {
                background: rgba(0, 0, 0, 0.9);
              }
            `}</style>
            <div className="fixture-list-container space-y-[10.4px]">
              {fixtures.map((f, i) => {
                const isSelected = selectedFixtureIds.includes(f.id);
                const accent = getLightTypeAccent(f.type, f.label);
                const selectedBg = isDarkMode ? hexToRgba(accent, 0.17) : hexToRgba(accent, 0.10);
                const src = String((f as any).source || 'auto');
                const isAIDetected = src === 'auto' || src === undefined;
                const isLocked = (f as any).locked !== undefined ? !!(f as any).locked : isAIDetected;
                return (
                  <button
                    key={f.id}
                    onClick={(e) =>
                      onSelectFixture(f.id, {
                        additive: e.shiftKey || e.metaKey,
                        range: e.ctrlKey && !e.shiftKey,
                      })
                    }
                    className={`
                      sidebar-btn-official-font flex items-center justify-between p-2 sm:p-3 border transition-all sidebar-blue-box-smaller font-bold min-h-[44px] sm:min-h-0 w-full
                      border-transparent
                      ${isSelected 
                        ? '' 
                        : f.isModified 
                          ? (isDarkMode ? 'bg-transparent hover:bg-white/5 text-white' : 'bg-transparent hover:bg-zinc-900/5 text-zinc-900') 
                          : (isDarkMode ? 'bg-transparent text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors' : 'bg-transparent text-zinc-600 hover:text-zinc-900 hover:bg-zinc-900/5 transition-colors')}
                    `}
                    style={{
                      borderRadius: '9px',
                      background: isSelected ? selectedBg : undefined,
                      borderColor: isSelected ? hexToRgba(accent, 0.39) : undefined,
                      boxShadow: isSelected ? `0 0 0 1px ${hexToRgba(accent, 0.13)} inset` : undefined,
                    }}
                  >
                    <span className="flex items-center gap-[3.9px]">
                      <span className="text-[13px] sm:text-[15px] font-black" style={{ color: accent }}>#{i+1}</span>
                      <span
                        style={isSelected ? { color: accent } : undefined}
                        className={`sidebar-blue-box-smaller font-normal ${!isSelected ? (f.isModified ? (isDarkMode ? 'text-white' : 'text-zinc-900') : (isDarkMode ? 'text-white/50' : 'text-zinc-600')) : ''}`}
                      >
                        {getDisplayName(f)}
                        <span
                          className={`text-[10px] font-normal ${isSelected ? '' : (isDarkMode ? 'text-zinc-500' : 'text-zinc-500')}`}
                          style={isSelected ? { color: accent } : undefined}
                        >
                          {language === 'zh'
                            ? `（${getListKelvinSummary(f)}K）`
                            : ` (${getListKelvinSummary(f)}K)`}
                        </span>
                      </span>
                    </span>
                    <div className="flex items-center gap-[5px] sm:gap-[7px]">
                      {onToggleLock && (
                        <span
                          role="button"
                          tabIndex={0}
                          title={isLocked ? tK('locked_double_click') : tK('unlocked_double_click')}
                          className={`shrink-0 inline-flex items-center justify-center rounded p-0.5 transition-colors ${
                            isDarkMode
                              ? 'text-zinc-400 hover:text-white hover:bg-white/10'
                              : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/60'
                          }`}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onToggleLock(f.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onToggleLock(f.id);
                            }
                          }}
                        >
                          {isLocked ? (
                            <svg className="w-[15px] h-[15px] sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-[15px] h-[15px] sm:w-4 sm:h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              aria-hidden
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                        </span>
                      )}
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full opacity-90" style={{ background: accent }}></div>}
                      {f.isModified && <span className="px-1 sm:px-1.5 py-0.5 rounded text-[7px] sm:text-[8px]" style={{ background: accent, color: '#101820' }}>{tK('adjusted')}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div
        className={`space-y-6 transition-all duration-500 ${
          !primaryFixture ? 'opacity-35 grayscale' : 'opacity-100'
        }`}
      >
        <div className={`flex items-center justify-between border-b pb-2 ${isDarkMode ? 'border-[#262626]' : 'border-zinc-200'}`}>
          <h3 className={`sidebar-btn-official-font sidebar-blue-box-smaller ${isDarkMode ? 'text-neutral-500' : 'text-zinc-600'}`}>
            {isMultipleSelected ? tReplace('batch_properties', language, { n: String(selectedFixtureIds.length) }) : tK('photon_properties')}
          </h3>
          <div className="flex items-center gap-[9px]">
            <button onClick={onReset} className={`sidebar-btn-official-font sidebar-blue-box-smaller transition-colors underline underline-offset-4 ${isDarkMode ? 'text-neutral-500 hover:text-white decoration-neutral-700' : 'text-zinc-600 hover:text-zinc-900 decoration-zinc-400'}`}>
              {tK('reset_all')}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3 group">
            <div className="flex justify-between items-end">
              <label className="sidebar-btn-official-font sidebar-blue-box-smaller tracking-wider text-neutral-400 group-hover:text-white transition-colors">{tK('intensity')}</label>
              <div className="flex items-center gap-[3px]">
                <input
                  type="text"
                  inputMode="decimal"
                  enterKeyHint="done"
                  autoComplete="off"
                  disabled={!primaryFixture}
                  value={intensityInput}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d.]/g, '');
                    setIntensityInput(value);
                  }}
                  onFocus={() => {
                    intensityFieldFocusedRef.current = true;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    intensityFieldFocusedRef.current = false;
                    const value = parseFloat(intensityInput);
                    if (isNaN(value) || value < 0) {
                      updateSelectedFixtures({ intensity: 0 });
                      setIntensityInput('0');
                    } else if (value > 200) {
                      updateSelectedFixtures({ intensity: 2.0 });
                      setIntensityInput('200');
                    } else {
                      updateSelectedFixtures({ intensity: value / 100 });
                      setIntensityInput(Math.round(value).toString());
                    }
                  }}
                  className={`sidebar-btn-official-font sidebar-blue-box-smaller ${isDarkMode ? 'text-[#D7CDA1] bg-black/20 border-[#D7CDA1]/25 focus:border-[#D7CDA1] focus:ring-[#D7CDA1]/40' : 'text-zinc-700 bg-white border-zinc-300 focus:border-zinc-400 focus:ring-zinc-200'} min-w-[44px] w-[48px] sm:w-[52px] px-[4px] py-1 text-right border rounded focus:outline-none focus:ring-1 transition-colors cursor-text disabled:opacity-40 disabled:cursor-not-allowed`}
                  style={{ borderRadius: '4px' }}
                  title={!primaryFixture ? (language === 'zh' ? '请先点击画布或列表选择一个光点' : 'Select a light point first') : undefined}
                  aria-label={tK('intensity')}
                />
                <span className={`text-[12px] font-mono ${isDarkMode ? 'text-[#D7CDA1]' : 'text-zinc-600'}`}>%</span>
              </div>
            </div>
            <div className="relative h-4 flex items-center">
              <input 
                type="range" min="0" max="2.0" step="0.01" 
                value={primaryFixture?.intensity || 0} 
                onChange={(e) => updateSelectedFixtures({ intensity: parseFloat(e.target.value) })} 
                className="w-full sidebar-warm-thumb" 
                style={{ touchAction: 'none' }}
                disabled={!primaryFixture}
              />
            </div>
          </div>

          <div className="space-y-3 group">
            <div className="flex justify-between items-end">
              <label className="sidebar-btn-official-font sidebar-blue-box-smaller tracking-wider text-neutral-400 group-hover:text-white transition-colors">{tK('color_temperature')}</label>
              <div className="flex items-center gap-[3px]">
                <input
                  type="text"
                  inputMode="numeric"
                  enterKeyHint="done"
                  autoComplete="off"
                  disabled={!primaryFixture}
                  value={colorTempInput}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d]/g, '');
                    setColorTempInput(value);
                  }}
                  onFocus={() => {
                    colorTempFieldFocusedRef.current = true;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => {
                    colorTempFieldFocusedRef.current = false;
                    const kelvin = parseFloat(colorTempInput);
                    if (isNaN(kelvin) || kelvin < 2000) {
                      updateSelectedFixtures({ colorTemp: -50 });
                      setColorTempInput('2000');
                    } else if (kelvin > 10000) {
                      updateSelectedFixtures({ colorTemp: 50 });
                      setColorTempInput('10000');
                    } else {
                      let colorTemp;
                      if (kelvin === 6500) {
                        colorTemp = 0;
                      } else if (kelvin < 6500) {
                        colorTemp = Math.round((kelvin - 6500) / 90);
                        colorTemp = Math.max(-50, colorTemp);
                      } else {
                        colorTemp = Math.round((kelvin - 6500) / 170);
                        colorTemp = Math.min(50, colorTemp);
                      }
                      updateSelectedFixtures({ colorTemp });
                      let actualKelvin;
                      if (colorTemp === 0) {
                        actualKelvin = 6500;
                      } else if (colorTemp < 0) {
                        actualKelvin = Math.round(6500 + (colorTemp * 90));
                      } else {
                        actualKelvin = Math.round(6500 + (colorTemp * 170));
                      }
                      setColorTempInput(actualKelvin.toString());
                    }
                  }}
                  className={`sidebar-btn-official-font sidebar-blue-box-smaller min-w-[52px] w-[56px] sm:w-[64px] px-[4px] py-1 text-right border rounded transition-colors focus:outline-none focus:ring-1 cursor-text disabled:opacity-40 disabled:cursor-not-allowed ${
                    (primaryFixture?.colorTemp ?? 0) > 0
                      ? 'text-blue-400 border-blue-500/35 bg-blue-500/10 focus:border-blue-400 focus:ring-blue-500/30'
                      : (primaryFixture?.colorTemp ?? 0) < 0
                        ? 'text-orange-400 border-orange-500/35 bg-orange-500/10 focus:border-orange-400 focus:ring-orange-500/30'
                        : isDarkMode
                          ? 'text-[#D7CDA1] border-[#D7CDA1]/25 bg-black/20 focus:border-[#D7CDA1] focus:ring-[#D7CDA1]/40'
                          : 'text-zinc-700 border-zinc-300 bg-white focus:border-zinc-400 focus:ring-zinc-200'
                  }`}
                  style={{ borderRadius: '4px' }}
                  title={!primaryFixture ? (language === 'zh' ? '请先点击画布或列表选择一个光点' : 'Select a light point first') : undefined}
                  aria-label={tK('color_temperature')}
                />
                <span className={`text-[12px] font-mono ${isDarkMode ? 'text-[#D7CDA1]' : 'text-zinc-600'}`}>K</span>
              </div>
            </div>
            <div className={`flex items-center justify-between text-[10px] px-[1px] ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
              <span>{t('Indoor', '室内')}</span>
              <span className={`${isDarkMode ? 'text-[#D7CDA1]/90' : 'text-zinc-700'} font-medium`}>6500K {t('Baseline', '基准')}</span>
              <span>{t('Outdoor', '室外')}</span>
            </div>
            <div className="relative h-4 flex items-center">
              <input 
                type="range" min="-50" max="50" step="1" 
                value={primaryFixture?.colorTemp || 0} 
                onChange={(e) => updateSelectedFixtures({ colorTemp: parseInt(e.target.value) })} 
                className="w-full sidebar-warm-thumb" 
                style={{ touchAction: 'none' }}
                disabled={!primaryFixture}
              />
            </div>
            {triPipelineSpatialReady ? (
              /* space-y-3 会给子项统一加 margin-top，单独设 mt-* 常被覆盖；用内边距把绿线整体下移 */
              <div className="pt-[60px]">
                <div
                  className="h-[3px] w-full rounded-full bg-[#4ade80] shadow-[0_0_10px_rgba(74,222,128,0.65)] animate-pulse motion-reduce:animate-none"
                  role="status"
                  title={
                    language === 'zh'
                      ? '深度图与法线图已就绪。'
                      : 'Depth and normal maps are ready.'
                  }
                />
              </div>
            ) : null}
          </div>

          {/* 让「0° 100% 50%」与上方色温基准线的垂直距离更大（目标约 148px） */}
          <div className="space-y-3 group" style={{ marginTop: 79 }}>
            <div className="flex justify-between items-end">
              <label className="sidebar-btn-official-font sidebar-blue-box-smaller tracking-wider text-neutral-400 group-hover:text-white transition-colors">{tK('color')}</label>
              <div className="flex items-center gap-[6px]">
                <span
                  className="w-4 h-4 rounded border border-white/20 shrink-0"
                  style={{
                    backgroundColor: (() => {
                      const h = primaryFixture?.hue ?? 0;
                      const s = primaryFixture?.saturation ?? 100;
                      const l = primaryFixture?.lightness ?? 50;
                      const { r, g, b } = hslToRgb(h, s, l);
                      return `rgb(${r},${g},${b})`;
                    })()
                  }}
                />
                <span className={`sidebar-btn-official-font sidebar-blue-box-smaller tabular-nums ${isDarkMode ? 'text-[#D7CDA1]' : 'text-zinc-700'}`}>
                  {primaryFixture?.hue ?? 0}° {primaryFixture?.saturation ?? 100}% {primaryFixture?.lightness ?? 50}%
                </span>
              </div>
            </div>
            <div className="sidebar-hsl-rows">
              {/* 蓝色框内 饱和?亮度/色相 三滑杆纵向间距：?.sidebar-hsl-rows ?gap:12px 强制控制 */}
              {/* ?行：饱和度（数字1位置?+ 滑条与色相等?*/}
              <div className="flex items-center gap-2">
                <span className="sidebar-hsl-label text-neutral-500 w-12 shrink-0">{tK('saturation')}</span>
                <div className="flex-1 flex items-center">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.max(0, Math.min(100, parseInt(saturationInput, 10) || 100))}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setSaturationInput(String(v));
                      updateSelectedFixtures({
                        hue: primaryFixture?.hue ?? 0,
                        saturation: v,
                        lightness: primaryFixture?.lightness ?? 50
                      });
                    }}
                    className="w-full"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={saturationInput}
                    onChange={(e) => setSaturationInput(e.target.value.replace(/[^\d]/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={() => {
                      let v = parseInt(saturationInput, 10);
                      if (Number.isNaN(v)) v = primaryFixture?.saturation ?? 100;
                      v = Math.max(0, Math.min(100, v));
                      setSaturationInput(String(v));
                      updateSelectedFixtures({
                        hue: primaryFixture?.hue ?? 0,
                        saturation: v,
                        lightness: primaryFixture?.lightness ?? 50,
                      });
                    }}
                    className={`sidebar-btn-official-font sidebar-blue-box-smaller w-9 sm:w-10 tabular-nums text-right bg-transparent border-b focus:outline-none px-0.5 py-0.5 ${isDarkMode ? 'text-[#D7CDA1] border-[#D7CDA1]/30 focus:border-[#D7CDA1]' : 'text-zinc-700 border-zinc-300 focus:border-zinc-500'}`}
                    style={{ borderRadius: '2px' }}
                  />
                  <span className={`text-[12px] font-mono ${isDarkMode ? 'text-[#D7CDA1]' : 'text-zinc-600'}`}>%</span>
                </div>
              </div>
              {/* ?行：亮度（数?位置?+ 滑条与色相等?*/}
              <div className="flex items-center gap-2">
                <span className="sidebar-hsl-label text-neutral-500 w-12 shrink-0">{tK('lightness')}</span>
                <div className="flex-1 flex items-center">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.max(0, Math.min(100, parseInt(lightnessInput, 10) || 50))}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setLightnessInput(String(v));
                      updateSelectedFixtures({
                        hue: primaryFixture?.hue ?? 0,
                        saturation: primaryFixture?.saturation ?? 100,
                        lightness: v
                      });
                    }}
                    className="w-full"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={lightnessInput}
                    onChange={(e) => setLightnessInput(e.target.value.replace(/[^\d]/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={() => {
                      let v = parseInt(lightnessInput, 10);
                      if (Number.isNaN(v)) v = primaryFixture?.lightness ?? 50;
                      v = Math.max(0, Math.min(100, v));
                      setLightnessInput(String(v));
                      updateSelectedFixtures({
                        hue: primaryFixture?.hue ?? 0,
                        saturation: primaryFixture?.saturation ?? 100,
                        lightness: v,
                      });
                    }}
                    className={`sidebar-btn-official-font sidebar-blue-box-smaller w-9 sm:w-10 tabular-nums text-right bg-transparent border-b focus:outline-none px-0.5 py-0.5 ${isDarkMode ? 'text-[#D7CDA1] border-[#D7CDA1]/30 focus:border-[#D7CDA1]' : 'text-zinc-700 border-zinc-300 focus:border-zinc-500'}`}
                    style={{ borderRadius: '2px' }}
                  />
                  <span className={`text-[12px] font-mono ${isDarkMode ? 'text-[#D7CDA1]' : 'text-zinc-600'}`}>%</span>
                </div>
              </div>
              {/* ?行：色相 + 滑条 */}
              <div className="flex items-center gap-2">
                <span className="sidebar-hsl-label text-neutral-500 w-12 shrink-0">{tK('hue')}</span>
                <div className="flex-1 relative h-4 flex items-center">
                  <div className="absolute inset-x-0 h-[5px] rounded-full top-1/2 -translate-y-1/2" style={{ background: 'linear-gradient(to right, #ff0000 0%, #ff8800 8%, #ffff00 17%, #88ff00 25%, #00ff00 33%, #00ff88 42%, #00ffff 50%, #0088ff 58%, #0000ff 67%, #8800ff 75%, #ff00ff 83%, #ff0088 92%, #ff0000 100%)' }} />
                  <input
                    type="range" min="0" max="360" step="1"
                    value={primaryFixture?.hue ?? 0}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      updateSelectedFixtures({ hue: v });
                      setHueInput(String(v));
                    }}
                    className="sidebar-color-hue-slider w-full relative z-10"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={hueInput}
                    onChange={(e) => setHueInput(e.target.value.replace(/[^\d]/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    onBlur={() => {
                      let v = parseInt(hueInput, 10);
                      if (Number.isNaN(v)) v = primaryFixture?.hue ?? 0;
                      v = ((v % 360) + 360) % 360;
                      setHueInput(String(v));
                      updateSelectedFixtures({ hue: v });
                    }}
                    className={`sidebar-btn-official-font sidebar-blue-box-smaller w-10 sm:w-11 tabular-nums text-right bg-transparent border-b focus:outline-none px-0.5 py-0.5 ${isDarkMode ? 'text-[#D7CDA1] border-[#D7CDA1]/30 focus:border-[#D7CDA1]' : 'text-zinc-700 border-zinc-300 focus:border-zinc-500'}`}
                    style={{ borderRadius: '2px' }}
                  />
                  <span className={`text-[12px] font-mono ${isDarkMode ? 'text-[#D7CDA1]' : 'text-zinc-600'}`}>°</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🔒🔒🔒 永久锁死代码 - 增加光源按钮 🔒🔒🔒 */}
        {/* ⚠️⚠️⚠️ 警告：此代码已被"永久锁死"，绝对禁止随意修改！ */}
        {/* 功能：增加光源按钮，在没有图片载入时禁用，只有载入图片后才能点击 */}
        {/* 锁死日期?026-02-11 */}
        {/* 🔒🔒🔒 未经用户明确要求，绝对禁止修改以下代?🔒🔒🔒 */}
        {/* 🔥 增加光源按钮 */}
        {onAddLightSource && (
          <div className="pt-3" style={{ marginTop: 48 }}>
            <button 
              onClick={() => setShowLightSourceOptions(!showLightSourceOptions)}
              disabled={!hasImage || isProcessing || isScanning}
              className={`w-full min-h-[72px] py-[14.4px] px-[9px] border border-dashed rounded-md bg-transparent transition-all flex items-center justify-center gap-[13px] group disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-neutral-800 hover:bg-neutral-900 hover:border-neutral-500' : 'border-zinc-300 hover:bg-zinc-100 hover:border-zinc-400'}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors group-hover:bg-[#D7CDA1] group-hover:text-black ${isDarkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-zinc-200 text-zinc-500'}`}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="4" x2="12" y2="20" /><line x1="4" y1="12" x2="20" y2="12" />
                </svg>
              </div>
              <span className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}>{tK('add_light_source')}</span>
            </button>
            
            {/* 🔥 16个光源选项面板?x4网格?*/}
            <div 
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showLightSourceOptions ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
              }`}
            >
              <div className="mx-5 mt-3 flex flex-col gap-2.5">
                {lightSourceTypes.map((item, index) => {
                  const isSelected = activeLightSourceIndex === index;
                  const accent = getLightTypeAccent(item.type);
                  const label = tK(item.labelKey as Parameters<typeof tI18n>[0]);
                  const labelParts = label.split(/\s+/);
                  const labelLine1 = labelParts[0] || label;
                  const labelLine2 = labelParts.length > 1 ? labelParts.slice(1).join(' ') : '';
                  return (
                    <button
                      key={`${item.type}-${index}`}
                      type="button"
                      onClick={() => {
                        setActiveLightSourceIndex(index);
                        if (onAddLightSource) {
                          onAddLightSource(item.type, label);
                        }
                      }}
                      className={`flex min-h-[72px] w-full min-w-0 flex-row items-center justify-center gap-3 rounded-xl border px-3 py-2.5 transition-all group ${
                        isSelected
                          ? 'bg-[#151515]'
                          : isDarkMode
                            ? 'border-[#1a1a1a] bg-[#0a0a0a] hover:bg-[#151515] hover:border-[#2a2a2a]'
                            : 'border-zinc-300 bg-zinc-100 hover:bg-zinc-200 hover:border-zinc-400'
                      }`}
                      style={isSelected ? { borderColor: accent, boxShadow: `0 0 0 1px ${hexToRgba(accent, 0.28)} inset` } : undefined}
                    >
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        isDarkMode ? 'border-[#222] bg-[#111] text-[#666] group-hover:text-white' : 'border-zinc-300 bg-white text-zinc-500 group-hover:text-zinc-900'
                        }`}
                      >
                        <LightSourceIcon type={item.type} />
                      </div>
                      <span
                        className={`text-[11px] font-medium text-left transition-colors ${
                        isDarkMode ? 'text-[#888] group-hover:text-[#ccc]' : 'text-zinc-600 group-hover:text-zinc-900'
                        }`}
                        style={{ lineHeight: 1.25 }}
                      >
                        {labelLine2 ? (
                          <>
                            {labelLine1}
                            <br />
                            {labelLine2}
                          </>
                        ) : (
                          label
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p
                className={`mx-5 mt-[calc(0.375rem+10px)] text-center text-[10px] leading-snug ${isDarkMode ? 'text-neutral-500' : 'text-zinc-500'}`}
              >
                {tK('add_light_downlight_scope_hint')}
              </p>
              {SHOW_MANUAL_LIGHT_SCENE_AND_DEBUG_CONTROLS && (
                <>
                  {isEditMode && selectedLightSourceType === 'cove_light' && onManualStripContextChange && (
                    <div className="mx-5 mt-2 mb-1">
                      <label
                        className={`block text-[11px] mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}
                      >
                        光带场景（影响蒙版走向与渲染约束）
                      </label>
                      <select
                        value={manualStripContext}
                        onChange={(e) => onManualStripContextChange(e.target.value as StripContext)}
                        className={`w-full text-[12px] rounded-lg border px-2 py-2 outline-none focus:ring-1 focus:ring-[#D7CDA1] ${
                          isDarkMode
                            ? 'bg-[#0d0d0d] border-[#2a2a2a] text-zinc-200'
                            : 'bg-white border-zinc-300 text-zinc-800'
                        }`}
                      >
                        {STRIP_CONTEXT_GROUPS.map((g) => (
                          <optgroup key={g.title} label={g.title}>
                            {g.ids.map((id) => (
                              <option key={id} value={id}>
                                {STRIP_CONTEXT_LABEL_ZH[id]}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  )}
                  {isEditMode && selectedLightSourceType === 'downlight' && onManualDownlightContextChange && (
                    <div className="mx-5 mt-2 mb-1">
                      <label
                        className={`block text-[11px] mb-1 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}
                      >
                        筒灯场景（影响控光提示与锁位）
                      </label>
                      <select
                        value={manualDownlightContext}
                        onChange={(e) => onManualDownlightContextChange(e.target.value as DownlightContext)}
                        className={`w-full text-[12px] rounded-lg border px-2 py-2 outline-none focus:ring-1 focus:ring-[#D7CDA1] ${
                          isDarkMode
                            ? 'bg-[#0d0d0d] border-[#2a2a2a] text-zinc-200'
                            : 'bg-white border-zinc-300 text-zinc-800'
                        }`}
                      >
                        {DOWNLIGHT_CONTEXT_GROUPS.map((g) => (
                          <optgroup key={g.title} label={g.title}>
                            {g.ids.map((id) => (
                              <option key={id} value={id}>
                                {DOWNLIGHT_CONTEXT_LABEL_ZH[id]}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className={`mx-5 mt-2 mb-2 p-2 rounded-xl border flex items-center justify-between ${isDarkMode ? 'border-[#232323] bg-[#0d0d0d]' : 'border-zinc-300 bg-zinc-100'}`}>
                    <div className={`${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'} text-[11px] leading-snug`}>
                      增加光源 A/B/C 调试（含筒灯；结果视图也显示）
                    </div>
                    <button
                      type="button"
                      onClick={() => setManualDebug8Enabled((v) => !v)}
                      className={`h-8 px-3 rounded-md border text-[12px] transition-all ${
                        manualDebug8Enabled
                          ? 'border-[#D7CDA1] text-[#D7CDA1] bg-[#171717]'
                          : isDarkMode
                            ? 'border-[#2a2a2a] text-zinc-400 hover:text-zinc-200 hover:border-zinc-500'
                            : 'border-zinc-300 text-zinc-600 hover:text-zinc-900 hover:border-zinc-400'
                      }`}
                      title="A=放置点；B=光束指向预测；C=结果相对原图的增亮质心。无结果图时仅 A/B。"
                    >
                      {manualDebug8Enabled ? '已开启' : '已关闭'}
                    </button>
                  </div>
                  <div className={`mx-5 mt-2 mb-2 p-2 rounded-xl border flex items-center justify-between ${isDarkMode ? 'border-[#232323] bg-[#0d0d0d]' : 'border-zinc-300 bg-zinc-100'}`}>
                    <div className={`${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'} text-[11px]`}>
                      识别暗光带 A/B/C 调试
                    </div>
                    <button
                      type="button"
                      onClick={() => setAutoCoveDebugEnabled((v) => !v)}
                      className={`h-8 px-3 rounded-md border text-[12px] transition-all ${
                        autoCoveDebugEnabled
                          ? 'border-[#7dd3fc] text-[#7dd3fc] bg-[#171717]'
                          : isDarkMode
                            ? 'border-[#2a2a2a] text-zinc-400 hover:text-zinc-200 hover:border-zinc-500'
                            : 'border-zinc-300 text-zinc-600 hover:text-zinc-900 hover:border-zinc-400'
                      }`}
                      title="仅作用于识别出来的槽灯/暗光带"
                    >
                      {autoCoveDebugEnabled ? '已开启' : '已关闭'}
                    </button>
                  </div>
                </>
              )}
            </div>
            
          </div>
        )}
      </div>

      {/* 按钮?- Nemo 皮肤（Generate / Upscale / Project Archive / User Account；LOAD PROJECT 已移?header 与蓝框同容器?*/}
      <div className={`space-y-3 pt-4 border-t relative z-[1] ${isDarkMode ? 'border-[#262626]' : 'border-zinc-200'}`} style={{ filter: 'none', opacity: 1 }}>
        {isAuthenticated && lightingPresets.length > 0 && (
          <div
            className={`rounded-md border p-3 space-y-2 ${isDarkMode ? 'border-[#262626] bg-[#0A0A0A]' : 'border-zinc-300 bg-white'}`}
          >
            <label
              className={`flex items-center gap-2 text-[10px] sm:text-[11px] cursor-pointer select-none ${isDarkMode ? 'text-neutral-300' : 'text-zinc-700'}`}
            >
              <input
                type="checkbox"
                className="rounded border-zinc-500"
                checked={useLightingPresetForRender}
                onChange={(e) => onUseLightingPresetForRenderChange?.(e.target.checked)}
              />
              <span>{tK('sidebar_use_saved_palette')}</span>
            </label>
            <select
              disabled={!useLightingPresetForRender}
              value={selectedLightingPresetId ?? ''}
              onChange={(e) =>
                onSelectedLightingPresetIdChange?.(e.target.value ? e.target.value : null)
              }
              className={`w-full text-[10px] sm:text-[11px] rounded-md border px-2 py-1.5 outline-none ${
                isDarkMode
                  ? 'border-[#333] bg-black text-neutral-200 disabled:opacity-40'
                  : 'border-zinc-300 bg-zinc-50 text-zinc-900 disabled:opacity-40'
              }`}
            >
              <option value="">{tK('sidebar_select_palette')}</option>
              {lightingPresets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {onRecognize && (
          <div
            ref={recognizeRowRef}
            className={`relative w-full${recognitionIdentifyExhausted ? ' pointer-events-none' : ''}`}
          >
            {recognizeAgainOpen &&
              !recognitionIdentifyExhausted &&
              createPortal(
                <div
                  id="nemo-recognize-again-popover"
                  role="dialog"
                  aria-modal="true"
                  style={recognizePopStyle}
                  className={`rounded-lg border shadow-2xl p-3 ${
                    isDarkMode
                      ? 'border-[#333] bg-[#141414] text-neutral-200'
                      : 'border-zinc-200 bg-white text-zinc-800 shadow-zinc-400/20'
                  }`}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <p className="sidebar-blue-box-smaller leading-snug">{tK('recognize_again_prompt')}</p>
                  <div className="mt-3 flex justify-end gap-2">
        <button 
                      type="button"
                      className={`sidebar-blue-box-smaller rounded-md px-3 py-1.5 border transition-colors ${
                        isDarkMode
                          ? 'border-[#333] text-neutral-300 hover:bg-white/5'
                          : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'
                      }`}
                      onClick={() => setRecognizeAgainOpen(false)}
                    >
                      {tK('btn_cancel_short')}
                    </button>
                    <button
                      type="button"
                      disabled={recognitionIdentifyExhausted}
                      className={
                        recognitionIdentifyExhausted
                          ? `sidebar-blue-box-smaller rounded-md px-3 py-1.5 font-medium transition-opacity opacity-50 ${
                              isDarkMode
                                ? 'bg-white/10 text-neutral-500 cursor-not-allowed'
                                : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                            }`
                          : 'sidebar-blue-box-smaller rounded-md px-3 py-1.5 bg-[#D7CDA1] text-[#101820] font-medium hover:opacity-90 transition-opacity'
                      }
                      title={recognitionIdentifyExhausted ? tK('recognize_quota_exhausted_hint') : undefined}
                      onClick={() => {
                        if (recognitionIdentifyExhausted) return;
                        setRecognizeAgainOpen(false);
                        void onRecognize();
                      }}
                    >
                      {tK('btn_continue')}
                    </button>
                  </div>
                </div>,
                document.body,
              )}
            <button
              type="button"
              disabled={!canRecognizeNow}
              title={
                recognitionIdentifyExhausted
                  ? tK('recognize_quota_exhausted_hint')
                  : undefined
              }
              onClick={() => {
                if (!canRecognizeNow) return;
                if (recognitionIdentifyExhausted) return;
                if (recognitionAlreadyCompleted) {
                  if (recognitionIdentifyExhausted) return;
                  setRecognizeAgainOpen(true);
                  return;
                }
                void onRecognize();
              }}
              className={`
            w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between transition-all duration-300 group
            ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A]' : 'border border-zinc-300 bg-white'}
            ${showRecognizeLockedUi ? (isDarkMode ? '!border-neutral-600 !bg-[#060606]' : '!border-zinc-400 !bg-zinc-200') : ''}
            ${!canRecognizeNow
              ? 'opacity-45 cursor-not-allowed saturate-[0.65]'
              : `${isDarkMode ? 'hover:border-[#D7CDA1]' : 'hover:bg-zinc-50 hover:border-[#D7CDA1]'} cursor-pointer`}
            disabled:pointer-events-none disabled:select-none
            ${shouldRecognizeBreathe ? 'recognize-btn-breathe' : ''}
          `}
            >
              <div className="flex items-center gap-[13px] min-w-0">
                <div
                  className={`w-9 h-9 shrink-0 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500' : 'bg-zinc-100 border-zinc-300 text-zinc-600'}
              ${!canRecognizeNow ? '' : `${isDarkMode ? 'group-hover:text-white group-hover:border-neutral-500' : 'group-hover:text-zinc-900 group-hover:border-zinc-400'}`}
              ${shouldRecognizeBreathe ? 'recognize-icon-breathe' : ''}`}
                >
                  {showRecognizeLockedUi ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </div>
                <span
                  className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors text-left min-w-0 leading-snug ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'} ${showRecognizeLockedUi ? (isDarkMode ? '!text-neutral-500' : '!text-zinc-500') : ''}`}
                >
                  {tK(showRecognizeLockedUi ? 'btn_recognize_locked' : 'btn_recognize')}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!recognitionAlreadyCompleted && !recognitionIdentifyExhausted && (
                  <div className={`${actionDotClass('bg-red-500', !canRecognizeNow)}`} />
                )}
                <div
                  className={`${isDarkMode ? 'text-neutral-700' : 'text-zinc-500'} ${!canRecognizeNow ? '' : 'group-hover:text-white'} transition-colors`}
                >
                  {showRecognizeLockedUi ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500" aria-hidden>
                      —
                    </span>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          </div>
        )}
        <button
          type="button"
          ref={generateButtonRef}
          disabled={!hasImage || isProcessing || isScanning}
          onClick={() => onEnhance()}
          className={`
            w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between transition-all duration-300 group
            ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A]' : 'border border-[#262626] bg-[#0A0A0A]'}
            ${!hasImage || isProcessing || isScanning
              ? 'opacity-80 cursor-default'
              : `${isDarkMode ? 'hover:border-[#D7CDA1] hover:shadow-[0_0_20px_rgba(215,205,161,0.15)]' : 'hover:bg-neutral-900 hover:border-neutral-700'} cursor-pointer`}
          `}
        >
          <div className="flex items-center gap-[13px]">
            <div
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500' : 'bg-zinc-100 border-zinc-300 text-zinc-600'}
              ${!hasImage || isProcessing || isScanning ? '' : `${isDarkMode ? 'group-hover:text-white group-hover:border-neutral-500' : 'group-hover:text-zinc-900 group-hover:border-zinc-400'}`}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors text-green-400 group-hover:text-green-300">
              {isProcessing && renderingProgressSource === 'generate'
                ? `${tK('rendering')} ${Math.max(1, Math.min(100, Math.round(renderingProgress)))}%`
                : hasRendered ? tK('re_render_pro') : tK('generate')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`${actionDotClass('bg-orange-500', !hasImage || isProcessing || isScanning)}`} />
            <div
              className={`${isDarkMode ? 'text-neutral-700' : 'text-neutral-700'} ${!hasImage || isProcessing || isScanning ? '' : 'group-hover:text-white'} transition-colors`}
            >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            </div>
          </div>
        </button>
        <button 
          onClick={onUpscale} 
          disabled={!hasImage} 
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
            <span className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}>{tK('upscale')}</span>
          </div>
          <div className={`${actionDotClass('bg-yellow-400', !hasImage)}`} />
        </button>
        <button
          type="button"
          onClick={() => onToneMapping?.()}
          disabled={!hasImage}
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M12 8.25a3.75 3.75 0 100 7.5 3.75 3.75 0 000-7.5z" />
              </svg>
            </div>
            <span
              className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}
            >
              {tK('tone_mapping')}
            </span>
          </div>
          <div className={`${actionDotClass('bg-green-500', !hasImage)}`} />
        </button>
        <button
          type="button"
          onClick={() => onAngleTransform?.()}
          disabled={!hasImage}
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}
            >
              {/* 角度图标（类似 60° 夹角） */}
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                {/* 两条射线 */}
                <path d="M6 18h12" />
                <path d="M6 18l7-12" />
                {/* 角弧 */}
                <path d="M9.2 17.6a3.8 3.8 0 0 1 1.6-3.2" />
                {/* 小刻度点 */}
                <circle cx="10.8" cy="15.2" r="0.6" fill="currentColor" stroke="none" />
              </svg>
            </div>
            <span
              className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}
            >
              {tK('angle_transform')}
            </span>
          </div>
          <div className={`${actionDotClass('bg-emerald-400', !hasImage)}`} />
        </button>
        <button
          type="button"
          onClick={() => onTextReconstruct?.()}
          disabled={!hasImage}
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 6h16" />
                <path d="M4 12h12" />
                <path d="M4 18h8" />
              </svg>
            </div>
            <span
              className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}
            >
              {tK('text_reconstruct')}
            </span>
          </div>
          <div className={`${actionDotClass('bg-lime-400', !hasImage)}`} />
        </button>
        <button
          type="button"
          onClick={() => onPhysicalReconstruct?.()}
          disabled={!hasImage}
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M7 3h10l4 6-9 12L3 9l4-6z" />
                <path d="M3 9h18" />
                <path d="M12 21V9" />
              </svg>
            </div>
            <span
              className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}
            >
              {tK('physical_reconstruct')}
            </span>
          </div>
          <div className={`${actionDotClass('bg-cyan-400', !hasImage)}`} />
        </button>
        <button
          type="button"
          disabled={!hasRendered}
          onClick={() => {
            void onOpenPhotopea?.();
          }}
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span
              className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}
            >
              {tK('photopea')}
            </span>
          </div>
          <div className={`${actionDotClass('bg-cyan-400', !hasRendered)}`} />
        </button>
        {/* 深度：侧栏入口隐藏（display:none），识别/渲染仍使用 depthImageDataUrl；DepthModal 可由程序 setIsDepthModalOpen 打开 */}
        <div className="hidden" aria-hidden>
          <button
            type="button"
            onClick={() => onDepth?.()}
            disabled={!hasImage}
            className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
          >
            <div className="flex items-center gap-[13px]">
              <div
                className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3.75 9.5h16.5M3.75 14.5h16.5M6 6.75h12a2.25 2.25 0 012.25 2.25v6a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 15v-6A2.25 2.25 0 016 6.75z" />
                </svg>
              </div>
              <span
                className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}
              >
                {tK('depth')}
              </span>
            </div>
            <div className={`${actionDotClass('bg-cyan-400', !hasImage)}`} />
          </button>
        </div>
        <button
          ref={archiveButtonRef}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenHistory?.(); }}
          className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center justify-between group transition-all cursor-pointer ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:bg-neutral-900 hover:border-neutral-700' : 'border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-zinc-400'}`}
        >
          <div className="flex items-center gap-[13px]">
            <div className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isDarkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-500 group-hover:text-white' : 'bg-zinc-100 border-zinc-300 text-zinc-600 group-hover:text-zinc-900'}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 transition-colors ${isDarkMode ? 'text-neutral-500 group-hover:text-white' : 'text-zinc-600 group-hover:text-zinc-900'}`}>{tK('project_archive')} ({historyCount})</span>
          </div>
          <div className={`${actionDotClass('bg-blue-500', false)}`} />
        </button>
        {onOpenUserAccount && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenUserAccount(); }}
            className={`w-full min-h-[72px] px-[9px] py-[14.4px] rounded-md flex items-center gap-[13px] group transition-all ${isDarkMode ? 'border border-[#262626] bg-[#0A0A0A] hover:border-[#D7CDA1]/30 hover:bg-neutral-900' : 'border border-zinc-300 bg-white hover:border-[#D7CDA1]/50 hover:bg-zinc-50'}`}
          >
            <div className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors group-hover:border-[#D7CDA1]/50 group-hover:text-[#D7CDA1] ${isDarkMode ? 'bg-neutral-800 text-white border-neutral-700' : 'bg-zinc-100 text-zinc-700 border-zinc-300'}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="text-left flex-1 sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95">
              <div className={`group-hover:text-[#D7CDA1] transition-colors ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{tK('user_account')}</div>
            </div>
            <div className="w-2 h-2 rounded-full bg-violet-500" />
          </button>
        )}
      </div>

      <div className={`mt-auto flex-1 min-h-0 border-t pt-6 pb-6 flex flex-col ${isDarkMode ? 'border-[#262626] bg-black' : 'border-zinc-200 bg-zinc-50'}`}>
        {/* User Guide - 使用说明面板内文字在现有基础上缩?30% */}
        <div className={`p-5 rounded-xl max-h-[280px] overflow-y-auto ${isDarkMode ? 'bg-[#080808] border border-neutral-800' : 'bg-zinc-100 border border-zinc-200'}`}>
          <h4 className="sidebar-btn-official-font sidebar-blue-box-smaller text-[#D7CDA1] mb-3 tracking-wider flex items-center gap-[7px]">
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {tK('user_guide')}
          </h4>
          <div className="space-y-2 sidebar-btn-official-font sidebar-blue-box-smaller text-neutral-500 leading-relaxed">
            <p><span className="text-[#D7CDA1] font-bold">1. {tK('upload_image')}</span> {tK('click_load_upload')}</p>
            <p><span className="text-[#D7CDA1] font-bold">2. {tK('auto_detect_lights')}</span> {tK('system_auto_detects')}</p>
            <p><span className="text-[#D7CDA1] font-bold">3. {tK('select_adjust')}</span></p>
            <p className="pl-[7px]">(1) {tK('click_select_ctrl')}</p>
            <p className="pl-[7px]">(2) {tK('click_reset_all')}</p>
            <p className="pl-[7px]">(3) {tK('recognized_layers')}</p>
            <p><span className="text-[#D7CDA1] font-bold">4. {tK('generate_effect')}</span> {tK('click_generate')}</p>
            <p><span className="text-[#D7CDA1] font-bold">5. {tK('unlock')}</span> {tK('double_click_undo')}</p>
            <p><span className="text-[#D7CDA1] font-bold">6. {tK('sidebar_guide_intensity_hsl_title')}</span> {tK('sidebar_guide_intensity_hsl')}</p>
            <p><span className="text-[#D7CDA1] font-bold">7. {tK('sidebar_guide_add_light_title')}</span> {tK('sidebar_guide_add_light_desc')}</p>
            <p><span className="text-[#D7CDA1] font-bold">8. {tK('hd_zoom')}</span> {tK('after_generation_hd')}</p>
            <p><span className="text-[#D7CDA1] font-bold">9. {tK('tone_mapping')}</span> {tK('tone_mapping_sidebar_guide')}</p>
            <p><span className="text-[#D7CDA1] font-bold">10. {tK('sidebar_guide_light_shadow_mapping_title')}</span> {tK('sidebar_guide_light_shadow_mapping_desc')}</p>
            <p><span className="text-[#D7CDA1] font-bold">11. {tK('sidebar_guide_text_reconstruct_title')}</span> {tK('sidebar_guide_text_reconstruct_desc')}</p>
            <p><span className="text-[#D7CDA1] font-bold">12. {tK('physical_reconstruct')}</span> {tK('sidebar_guide_physical_reconstruct_desc')}</p>
            <p><span className="text-[#D7CDA1] font-bold">13. {tK('sidebar_guide_photopea_title')}</span> {tK('sidebar_guide_photopea_desc')}</p>
            <p><span className="text-[#D7CDA1] font-bold">14. {tK('sidebar_guide_chat_title')}</span> {tK('sidebar_guide_chat_desc')}</p>
            <p className="text-[#D7CDA1]/90 mt-2">{tK('hint_ai_vary')}</p>
          </div>
        </div>
        <p className={`mt-3 sidebar-btn-official-font sidebar-footer-blue-box leading-relaxed flex items-center gap-1.5 ${isDarkMode ? 'text-neutral-500' : 'text-zinc-600'}`}>
          <svg className="shrink-0 text-[#D7CDA1]" width={22} height={11} viewBox="0 0 500 250" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M50,125 C100,50 350,50 400,125 C350,200 100,200 50,125 Z" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M85,100 C95,110 95,140 85,150" stroke="currentColor" strokeWidth="3" />
            <path d="M100,90 C115,110 115,140 100,160" stroke="currentColor" strokeWidth="3" />
            <circle cx="75" cy="115" r="5" stroke="currentColor" strokeWidth="2" />
            <g stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M130,160 L130,90 L170,160 L170,90" />
              <path d="M190,160 L190,90 L230,90 M190,125 L220,125 M190,160 L230,160" />
              <path d="M250,160 L250,90 L280,130 L310,90 L310,160" />
              <ellipse cx="350" cy="125" rx="25" ry="35" />
            </g>
            <path d="M400,125 L470,80 L470,170 Z" stroke="currentColor" strokeWidth="4" />
          </svg>
          <span className="text-[#D7CDA1] font-bold" style={{ letterSpacing: '-0.054em' }}>NEMOLIGHTLAB</span>
        </p>
        <p className={`mt-2 sidebar-btn-official-font sidebar-footer-blue-box ${isDarkMode ? 'text-neutral-500' : 'text-zinc-600'}`}>{tK('feedback_email')}</p>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setFeedbackEmailDialogOpen(true)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:text-[#D7CDA1] hover:border-[#D7CDA1]/50 ${isDarkMode ? 'bg-neutral-800 border border-neutral-700 text-neutral-500' : 'bg-zinc-200 border border-zinc-300 text-zinc-600'}`}
            title={FEEDBACK_EMAIL_ADDRESS}
            aria-label={FEEDBACK_EMAIL_ADDRESS}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </button>
        </div>
      </div>
      </div>
      </div>
      </div>
    </div>
    {feedbackEmailDialogOpen &&
      typeof document !== 'undefined' &&
      createPortal(
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center p-4 bg-black/55"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nemo-feedback-email-dialog-title"
          onClick={() => setFeedbackEmailDialogOpen(false)}
        >
          <div
            className={`max-w-[min(92vw,380px)] w-full rounded-xl border p-5 shadow-xl ${
              isDarkMode ? 'border-neutral-700 bg-neutral-900 text-white' : 'border-zinc-200 bg-white text-zinc-900'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="nemo-feedback-email-dialog-title" className="text-sm font-semibold text-[#D7CDA1] mb-3">
              {tK('feedback_email_dialog_title')}
            </h2>
            <p className="text-lg font-mono font-medium break-all select-all mb-4">{FEEDBACK_EMAIL_ADDRESS}</p>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                className={`px-3 py-2 rounded-md text-sm font-medium border ${
                  isDarkMode
                    ? 'border-neutral-600 text-neutral-200 hover:bg-neutral-800'
                    : 'border-zinc-300 text-zinc-800 hover:bg-zinc-50'
                }`}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(FEEDBACK_EMAIL_ADDRESS);
                    alert(tK('feedback_email_copied'));
                  } catch {
                    alert(FEEDBACK_EMAIL_ADDRESS);
                  }
                }}
              >
                {tK('feedback_email_copy')}
              </button>
              <a
                href={`mailto:${FEEDBACK_EMAIL_ADDRESS}`}
                className="px-3 py-2 rounded-md text-sm font-medium bg-[#D7CDA1] text-black hover:opacity-90"
                onClick={() => setFeedbackEmailDialogOpen(false)}
              >
                {tK('feedback_email_open_client')}
              </a>
              <button
                type="button"
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  isDarkMode ? 'text-neutral-400 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'
                }`}
                onClick={() => setFeedbackEmailDialogOpen(false)}
              >
                {tK('tone_mapping_close')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default Sidebar;
